import React, { useEffect, useState } from "react";
import { CreditCard, CheckCircle2, AlertCircle, Loader2, ExternalLink } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminPayouts() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/payments/connect/status").then(({ data }) => setStatus(data))
      .catch(() => setStatus({ connected: false }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const ready = status?.connected && status?.charges_enabled && status?.payouts_enabled;

  return (
    <div className="max-w-2xl" data-testid="admin-payouts">
      <h1 className="font-heading font-extrabold text-3xl mb-1">Payouts</h1>
      <p className="text-sm text-muted-foreground mb-6">Connect your branch's Stripe account so card payments for your orders are paid directly to you.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-metros-red/10 text-metros-red rounded-xl p-3"><CreditCard size={22} /></div>
            <div>
              <p className="font-heading font-bold text-lg">Stripe Payouts</p>
              {ready ? (
                <span data-testid="payout-ready" className="inline-flex items-center gap-1.5 text-sm font-bold text-green-700"><CheckCircle2 size={16} /> Connected & receiving payouts</span>
              ) : status?.connected ? (
                <span data-testid="payout-pending" className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-600"><AlertCircle size={16} /> Onboarding incomplete</span>
              ) : (
                <span data-testid="payout-none" className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-500"><AlertCircle size={16} /> Not connected</span>
              )}
            </div>
          </div>

          {status?.connected && (
            <ul className="text-sm space-y-1 mb-5">
              <li className="flex items-center gap-2">{status.details_submitted ? <CheckCircle2 size={14} className="text-green-600" /> : <AlertCircle size={14} className="text-amber-500" />} Details submitted</li>
              <li className="flex items-center gap-2">{status.charges_enabled ? <CheckCircle2 size={14} className="text-green-600" /> : <AlertCircle size={14} className="text-amber-500" />} Card payments enabled</li>
              <li className="flex items-center gap-2">{status.payouts_enabled ? <CheckCircle2 size={14} className="text-green-600" /> : <AlertCircle size={14} className="text-amber-500" />} Bank payouts enabled</li>
            </ul>
          )}

          {!ready && (
            <button data-testid="connect-stripe-btn" onClick={onboard} disabled={busy}
              className="bg-metros-red hover:bg-metros-redhover text-white rounded-full px-6 py-3 font-bold flex items-center gap-2 disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <ExternalLink size={18} />}
              {status?.connected ? "Continue Stripe onboarding" : "Connect Stripe"}
            </button>
          )}
          {ready && <button data-testid="refresh-payout" onClick={load} className="text-sm font-semibold text-metros-red underline">Refresh status</button>}
        </div>
      )}
    </div>
  );
}
